import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { authzAudience, authzScope } from "../../auth/auth0";
import axios from "axios";
import { useDispatch } from "react-redux";
import * as Sentry from "@sentry/react";
import { getTeamInformation } from "../../store/actions/team.actions";
import { AuthLoading } from "./AuthLoading";
import { AuthFail } from "./AuthFail";
import { useNavigate } from "react-router";
import {
  getManagerTeamList,
  selectManagerTeam,
} from "../../store/actions/manager.actions";
import { useSelector } from "react-redux";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { REAUTH_ERRORS, setAccessTokenGetter } from "@api/tokenProvider";
import { getBanners } from "@store/actions/banner.actions";

const publicRoutes = new Set(["/", "/login"]);
const validRedirects = new Set(["/help-center"]);

// After a non-reauth token refresh failure (e.g. Auth0 unreachable), skip
// refresh attempts for this long — each attempt can block a request for up
// to ~30s (3 retries x 10s timeout) behind auth0-spa-js's cross-tab lock.
// Trade-off: if the session dies during the window, requests 401 with the
// stale header until it lapses; the next attempt then redirects to login.
const TOKEN_REFRESH_COOLDOWN_MS = 30000;
let tokenRefreshFailedAt = 0;

// loginWithRedirect resolves when navigation to Auth0 STARTS, not when it
// commits — the page normally unloads moments later. The grace period must
// comfortably exceed a slow authorize-redirect commit: if it lapses while
// the first navigation is still in flight, a late request could start a
// second redirect and clobber auth0-spa-js's transaction slot ("Invalid
// state" on callback). 30s trades a slower Esc-cancel recovery for that.
const REDIRECT_GRACE_MS = 30000;

// Single-flight: concurrent failing requests must not each start a redirect —
// parallel loginWithRedirect calls clobber auth0-spa-js's one transaction
// slot (state/nonce) and break the callback with "Invalid state". Everyone
// awaits the same promise, so a failed redirect rejects every waiter (not
// just the initiator), and a cancelled navigation (Esc/Stop leaves the page
// alive) unwedges after the grace period instead of suppressing retries.
let redirectPromise = null;

// Cancelled-navigation retry timer (Esc/Stop leaves the page alive with the
// spinner up). The pageshow handler clears it before reloading a bfcache
// restore so an overdue timer can never race the reload; after system
// sleep/resume it fires late and retries, which is the desired recovery.
let redirectRetryTimer = null;

const redirectToLogin = (loginWithRedirect) => {
  if (!redirectPromise) {
    redirectPromise = loginWithRedirect({
      appState: {
        returnTo: window.location.pathname + window.location.search,
      },
    })
      .then(() => {
        setTimeout(() => {
          redirectPromise = null;
        }, REDIRECT_GRACE_MS);
      })
      .catch((e) => {
        redirectPromise = null;
        Sentry.captureException(e);
        throw e;
      });
  }
  return redirectPromise;
};

// Shared token acquisition for the axios interceptor and fetch-based callers
// (via tokenProvider), so both get identical cooldown/redirect/Sentry
// semantics. Never rejects: returns a fresh token, or null meaning "send
// with the last known header".
const acquireRequestToken = async (getAccessTokenSilently, loginWithRedirect) => {
  if (Date.now() - tokenRefreshFailedAt < TOKEN_REFRESH_COOLDOWN_MS) {
    return null;
  }
  try {
    return await getAccessTokenSilently();
  } catch (e) {
    if (REAUTH_ERRORS.has(e.error)) {
      try {
        await redirectToLogin(loginWithRedirect);
        // Navigation is underway — hold the request so it doesn't 401 and
        // flash toasts mid-redirect. If the user cancels the navigation the
        // hold expires and the request fails through the normal HTTP path,
        // keeping the error shape axios callers expect (e.response.*).
        await new Promise((resolve) => setTimeout(resolve, REDIRECT_GRACE_MS));
      } catch {
        // Redirect failed (captured in redirectToLogin); let the request
        // fire and surface through the caller's normal HTTP error handling.
      }
      return null;
    }
    Sentry.captureException(e);
    tokenRefreshFailedAt = Date.now();
    return null;
  }
};

// auth0-spa-js keeps the signed-in account in localStorage under
// "@@auth0spajs@@::<clientId>::@@user@@", shared by every tab. The team header
// lives in this tab's axios defaults. A login as another account in a second
// tab swaps the cache under this tab, which then sends the new account's token
// with the old account's team and gets 403s from then on. The listener below
// reloads this tab when that entry changes account or disappears (logout):
// the boot path reads the stored team again and validates it against the new
// account. A token refresh rewrites the entry with the same sub and is
// ignored. A team switch writes team-id, which is not watched.
const AUTH0_CACHE_PREFIX = "@@auth0spajs@@";
const AUTH0_USER_KEY_SUFFIX = "::@@user@@";

// logout() clears the cache before it navigates to Auth0's logout endpoint.
// A tab that reloads at once lands on /login, which forwards to Auth0 at
// once, and can sign in again before that endpoint ends the session. The
// delay lets the logout finish first.
const LOGOUT_RELOAD_DELAY_MS = 2000;

const isAuth0UserKey = (key) =>
  typeof key === "string" &&
  key.startsWith(AUTH0_CACHE_PREFIX) &&
  key.endsWith(AUTH0_USER_KEY_SUFFIX);

// Returns null for a removed entry or any shape this code does not know.
const cachedAccountSub = (value) => {
  if (!value) {
    return null;
  }
  try {
    const decoded = JSON.parse(value).decodedToken;
    return decoded?.claims?.sub ?? decoded?.user?.sub ?? null;
  } catch {
    return null;
  }
};

const trimPathName = (pathname) => {
  if (pathname[pathname.length - 1] === "/") {
    return pathname.substring(0, pathname.length - 1);
  }
  return pathname;
};

export const Auth = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [authLoading, setAuthLoading] = useState(true);
  const [authFailed, setAuthFailed] = useState(false);
  const {
    user,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    loginWithRedirect,
  } = useAuth0();
  const { teams } = useSelector(({ manager }) => manager);
  const {
    loading: { info },
    info: {
      display_name: teamName,
    }
  } = useSelector(({ team }) => team);


  const initialize = async (token) => {
    axios.defaults.headers.common["Authorization"] = token;
    Sentry.setUser(user?.email ? { email: user?.email } : null);
    await dispatch(getManagerTeamList());
    dispatch(getBanners());
  };

  const attachToken = async () => {
    setAuthLoading(true);
    setAuthFailed(false);
    let redirecting = false;
    try {
      const token = await getAccessTokenSilently();
      await initialize(token);
    } catch (e) {
      if (e.error === "consent_required") {
        try {
          const token = await getAccessTokenWithPopup({
            audience: authzAudience,
            scope: authzScope,
            prompt: "consent",
          });
          await initialize(token);
        } catch (popupErr) {
          Sentry.captureException(popupErr);
          message.error("We couldn't complete sign-in. Please try logging in again.");
          setAuthFailed(true);
        }
      } else if (REAUTH_ERRORS.has(e.error)) {
        // The session is dead but Auth0's cached state still reports
        // isAuthenticated, so the /login redirect effect below never fires.
        // A full redirect login is the only way to recover a usable session.
        try {
          await redirectToLogin(loginWithRedirect);
          redirecting = true;
          // If the user cancels the navigation (Esc/Stop) the page never
          // unloads — retry so the spinner can't wedge forever. The timer
          // dies with the page once navigation commits, and the pageshow
          // handler clears it on a bfcache restore.
          clearTimeout(redirectRetryTimer);
          redirectRetryTimer = setTimeout(() => attachToken(), REDIRECT_GRACE_MS + 1000);
        } catch {
          // Captured in redirectToLogin — e.g. blocked storage in private
          // browsing. Release the spinner and show the failure state.
          message.error("We couldn't verify your session. Please try again.");
          setAuthFailed(true);
        }
      } else {
        Sentry.captureException(e);
        message.error("We couldn't verify your session. Please try again.");
        setAuthFailed(true);
      }
    } finally {
      // Keep the loading screen up while the browser navigates to Auth0 —
      // otherwise the app flashes and fires tokenless API calls.
      if (!redirecting) setAuthLoading(false);
    }
  };

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      attachToken();
    } else {
      setAuthLoading(false);
    }
  }, [isLoading, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    // The token set in initialize() goes stale after expiry; refresh it per
    // request. getAccessTokenSilently caches, so this is a no-op until then.
    const id = axios.interceptors.request.use(async (config) => {
      const token = await acquireRequestToken(getAccessTokenSilently, loginWithRedirect);
      if (token) {
        config.headers.Authorization = token;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(id);
  }, [isAuthenticated]);

  useEffect(() => {
    setAccessTokenGetter(() =>
      acquireRequestToken(getAccessTokenSilently, loginWithRedirect)
    );
  }, [getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    // If the user backs out of the Auth0 login page, bfcache can restore the
    // app frozen mid-redirect (spinner up, stale timers). Reload for a clean
    // boot: a session recovered in another tab logs straight in; a still-dead
    // session redirects back to Auth0. That forward bounce is architectural —
    // the app has no unauthenticated resting page (Login.jsx itself forwards
    // to Auth0) — so don't logout() here: it would only clobber the shared
    // localstorage cache and kill sessions freshly minted by other tabs.
    const onPageShow = (e) => {
      if (e.persisted && redirectPromise) {
        clearTimeout(redirectRetryTimer);
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  useEffect(() => {
    // The storage event fires only in the other tabs, never in the tab that
    // made the change, so the tab that logged in or out keeps its own flow.
    let logoutReloadTimer = null;
    const onStorage = (e) => {
      if (!isAuth0UserKey(e.key)) {
        return;
      }
      const oldSub = cachedAccountSub(e.oldValue);
      const newSub = cachedAccountSub(e.newValue);
      if (oldSub === newSub) {
        return;
      }
      if (newSub === null) {
        clearTimeout(logoutReloadTimer);
        logoutReloadTimer = setTimeout(() => window.location.reload(), LOGOUT_RELOAD_DELAY_MS);
        return;
      }
      window.location.reload();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      clearTimeout(logoutReloadTimer);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    const teamId = localStorage.getItem("team-id");
    if (teamId && teams.length) {
      dispatch(selectManagerTeam(teamId, navigate));
    }
  }, [teams]);

  useEffect(() => {
    const path = window.location.pathname;
    if ((!isLoading && isAuthenticated && !authLoading)) {
      if (publicRoutes.has(path)) {
        navigate("/team-selection");
      }
    }

    if (!isLoading && !isAuthenticated && !authLoading) {
      if (!publicRoutes.has(path)) { 
        if (!validRedirects.has(trimPathName(path))) {
          navigate("/login");
        } else {
          // Only allow post login redirect to validRedirects paths.
          redirectToLogin(loginWithRedirect)
            .then(() => {
              // Same cancelled-navigation recovery as attachToken: if the
              // page is still alive after the grace period, reload to re-run
              // this effect rather than wedging on the loading screen.
              clearTimeout(redirectRetryTimer);
              redirectRetryTimer = setTimeout(
                () => window.location.reload(),
                REDIRECT_GRACE_MS + 1000
              );
            })
            .catch(() => {
              // Captured in redirectToLogin. Without this the user would sit
              // on the loading screen forever.
              message.error("We couldn't complete sign-in. Please try logging in again.");
              setAuthFailed(true);
            });
        }
      }
    }
  }, [isLoading, isAuthenticated, authLoading])

  const authReady = !authLoading && !isLoading && (isAuthenticated || publicRoutes.has(window.location.pathname));

  return (
    <>
      {!authReady ? (
        authFailed ? <AuthFail /> : <AuthLoading />
      ) : (
        children
      )}
    </>
  );
};
